<template>
  <div class="flex flex-col gap-4">
    <div class="tb-form-group" v-if="!isDataMapped || type === 'marquee'">
      <label class="tb-label mb-2 block">
        Background Color
      </label>

      <div class="mb-3 flex items-center gap-4">
        <div class="tb-radio mr-3">
          <input
            type="radio"
            id="solid"
            value="solid"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-solid"
          />

          <label class="radio inline-radio" for="solid">Solid</label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="gradient"
            value="gradient"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-gradient"
          >

          <label class="radio inline-radio" for="gradient">Gradient</label>
        </div>
      </div>

      <template v-if="fillStyle === 'solid'">
        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker"
          data-entity="producer-editor-toggleBackgroundColorPicker"
          data-test="graphics-editor-background-color"
        >
          <colorpicker
            :color="backgroundColor"
            data-test="graphics-editor-background-color"
            :large="true"
            show-buttons
            @change="updateField('backgroundColor', $event)"
          />
        </div>
      </template>

      <template v-else>
        <div class="tb-form-group flex flex-nowrap items-center gap-3">
          <div class="input-group flex-none inline-flex w-[72px] overflow-hidden">
            <input
              class="tb-input"
              type="number"
              name="gradientDirection"
              v-model.number="gradientDirection"
              data-entity="producer-editor-gradientDirection"
              data-test="graphics-editor-gradient-direction"
            />

            <span class="input-group-addon symbol">°</span>
          </div>

          <span class="ml-3" data-entity="producer-editor-toggleGradientColorOnePicker">
          <colorpicker
            :color="gradientColorOne"
            data-test="graphics-editor-gradient-color-one"
            :large="true"
            show-buttons
            @change="updateField('gradientColorOne', $event)"
          />
          </span>

          <span class="ml-3" data-entity="producer-editor-toggleGradientColorTwoPicker">
          <colorpicker
            :color="gradientColorTwo"
            data-test="graphics-editor-gradient-color-two"
            :large="true"
            show-buttons
            @change="updateField('gradientColorTwo', $event)"
          />
          </span>
        </div>
      </template>
    </div>

    <div v-else v-html="mappingLabel" />

    <div v-if="!isDataMapped || type === 'marquee'" class="tb-form-group">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="backgroundImage"
          v-model="backgroundImage"
          data-entity="producer-editor-toggleBackgroundImage"
          data-test="graphics-editor-background-image"
        />

        <label for="backgroundImage">Background image</label>
      </div>
    </div>

    <div v-if="backgroundImage && (!isDataMapped || type==='marquee')">
      <div class="tb-form-group">
        <div v-if="backgroundImageUrl" class="media-upload-wrapper">
          <div
            class="media"
            :style="{
                backgroundImage: `url('${backgroundImageUrl}')`,
              }"
          />

          <div class="edit-media">
            <button
              class="tb-btn tb-btn-contained on-image"
              data-entity="producer-editor-editBackgroundImage"
              data-test="graphics-editor-backgroundImage-select"
              @click="selectAsset"
            >
              <span class="fas fa-pencil-alt"></span>
            </button>
          </div>
        </div>

        <button
          v-else
          class="tb-btn tb-btn-contained medium-em"
          data-entity="producer-editor-selectBackgroundImage"
          data-test="graphics-editor-backgroundImage-select"
          @click="selectAsset"
        >
          <span class="fas fa-image mr-1"></span>
          Choose image
        </button>
      </div>

      <div v-if="backgroundImageUrl" class="tb-form-group">
        <div class="tb-radio">
          <input
            type="radio"
            id="cover"
            value="cover"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-cover"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label
            class="radio inline-radio"
            for="cover"
            @click.prevent="backgroundSize = 'cover'"
          >
            Fill image
          </label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="contain"
            value="contain"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-fit"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label
            class="radio inline-radio"
            for="contain"
            @click.prevent="backgroundSize = 'contain'"
          >
            Fit image
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash-es';

export default {
  props: {
    element: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },
  },

  computed: {
    isDataMapped() {
      return !!get(this.element, 'data.provider');
    },

    type() {
      return this.element.type;
    },

    mappingLabel() {
      if (!this.isDataMapped) {
        return '';
      }

      let dataMessage = 'Data is mapped';

      const { dataKeyMap } = this.element.data;

      if (!dataKeyMap) {
        return '';
      }

      Object.keys(dataKeyMap).forEach((key) => {
        dataMessage = `${dataMessage  }<br>${ key }: ${ dataKeyMap[key] }`;
      });

      return dataMessage;
    },

    backgroundColor() {
      return this.elementFields?.backgroundColor ?? this.element.backgroundColor ?? '#0F84FB';
    },

    gradientColorOne() {
      return this.elementFields?.gradientColorOne ?? this.element.gradientColorOne ?? '#00c4dd';
    },

    gradientColorTwo() {
      return this.elementFields?.gradientColorTwo ?? this.element.gradientColorTwo ?? '#0f84fb';
    },

    backgroundImage: {
      get() {
        return this.elementFields?.backgroundImage ?? this.element.backgroundImage ?? false;
      },

      set(value) {
        this.updateField('backgroundImage', value);
      },
    },

    backgroundSize: {
      get() {
        return this.elementFields?.backgroundSize ?? this.element.backgroundSize ?? 'cover';
      },

      set(value) {
        this.updateField('backgroundSize', value);
      },
    },

    backgroundImageUrl() {
      return this.elementFields?.backgroundImageUrl ?? this.element.backgroundImageUrl ?? '';
    },

    fillStyle: {
      get() {
        return this.elementFields?.fillStyle ?? this.element.fillStyle ?? 'solid';
      },

      set(value) {
        this.updateField('fillStyle', value);
      },
    },

    gradientDirection: {
      get() {
        // TODO: Replace magic number
        return this.elementFields?.gradientDirection ?? this.element.gradientDirection ?? 180;
      },

      set(value) {
        this.updateField('gradientDirection', value);
      },
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },
  },

  methods: {
    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: 'image' });

      window.assetmanfn = (asset, meta = {}) => {
        this.element.backgroundImageUrl = asset.media_url; // eslint-disable-line vue/no-mutating-props
        this.element.backgroundImageMeta = meta; // eslint-disable-line vue/no-mutating-props
        this.updateField('backgroundImageUrl', asset.media_url);
        this.updateField('backgroundImageMeta', meta);
        this.$store.dispatch('toggleAssetModal', { toggled: false, type: 'image' });
      };
    },
  },
};
</script>
